import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Spinner } from 'react-bootstrap';
import dayjs from 'dayjs';
import { EVA_URL } from 'helpers/urlHelper';
import { getRemakesCheckedSummary } from 'services/productionOverviewService';
import {
  dedupeItems,
  getRightFirstTimePercent,
  isUNumberJob,
  normalizeRemakeProductType,
  PRODUCT_TYPE_LABELS,
  summarizeRemakeItemsByProductType
} from './qualitySummaryHelpers';
import QualitySummaryCard from './QualitySummaryCard';
import QualityItemsDetailModal from './QualityItemsDetailModal';

const CCF_ODR_PRODUCT_TYPE_STR =
  '494,498,483,493,509,506,492,508,6235,503,6974,496,478,486,480,487,490,510,488,499,489,511,484,473,618,491,474,2068,475,482,6234,502,1651,13361,1170,501,479,504,485,481,476,505,497,1943';

function buildCcfOdrReportUrl(date) {
  const formattedDate = dayjs(date, 'YYYY-MM-DD').subtract(1, 'day').format('DD/MM/YYYY');
  return `${EVA_URL}/Internal/Reports/CCFODR.aspx?datetime=${formattedDate}&productTypeStr=${CCF_ODR_PRODUCT_TYPE_STR}`;
}

const REMAKE_ITEM_COLUMNS = [
  { key: 'jobNo', label: 'Job No' },
  { key: 'boxId', label: 'Box Id' },
  { key: 'winNo', label: 'Win No' },
  { key: 'typeName', label: 'Type' },
  {
    key: 'category',
    label: 'Category',
    render: item => {
      const type = normalizeRemakeProductType(item.typeName);
      return type ? PRODUCT_TYPE_LABELS[type] : 'Other (not counted in any card)';
    }
  },
  { key: 'status', label: 'Status', render: item => item.status || '—' },
  { key: 'checkedBy', label: 'Checked By', render: item => item.checkedBy || '—' },
  { key: 'created', label: 'Created' }
];

export default function QualityRemakesSummary({ date }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const remakesDate = dayjs(date, 'YYYY-MM-DD').subtract(1, 'day').format('YYYY-MM-DD');
    getRemakesCheckedSummary(remakesDate)
      .then(result => {
        if (cancelled) return;
        const data = result?.data?.data ?? result?.data ?? [];
        setItems(Array.isArray(data) ? data : []);
      })
      .catch(err => {
        if (!cancelled) console.error('Error fetching remakes checked summary', err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [date]);

  const filteredItems = useMemo(() => dedupeItems(items).filter(item => !isUNumberJob(item.jobNo)), [items]);
  const summary = useMemo(() => summarizeRemakeItemsByProductType(items), [items]);

  const cards = useMemo(
    () =>
      summary.groups.map(item => ({
        ...item,
        rftPercent: getRightFirstTimePercent(item.totalChecked, item.totalRejected)
      })),
    [summary]
  );

  return (
    <section className="quality-summary-section" style={{ marginTop: 20 }}>
      <div style={{ marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
          <h4 className="quality-summary-section-title" style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>
            Remakes Checked Summary
          </h4>
          <span
            className="quality-summary-status quality-summary-stat-clickable"
            role="button"
            tabIndex={0}
            onClick={() => setDetail({ title: 'All Remake Jobs Today', items: filteredItems })}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setDetail({ title: 'All Remake Jobs Today', items: filteredItems });
              }
            }}
            style={{ fontSize: 12 }}
          >
            Total remake jobs today: <strong>{summary.totalRemake}</strong>
          </span>
        </div>
        <a href={buildCcfOdrReportUrl(date)} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12 }}>
          View CCF/ODR Report
        </a>
      </div>

      {loading ? (
        <div className="text-center py-3">
          <Spinner variant="primary" animation="border" role="status" />
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
          {cards.map(c => (
            <QualitySummaryCard
              key={c.key}
              label={c.label}
              rftPercent={c.rftPercent}
              stats={[
                {
                  label: 'Total Remake',
                  value: c.totalRemake,
                  onClick: () => setDetail({ title: `${c.label} — Total Remake`, items: c.totalRemakeItems })
                },
                {
                  label: 'To Check',
                  value: c.totalToCheck,
                  onClick: () => setDetail({ title: `${c.label} — To Check`, items: c.toCheckItems })
                },
                {
                  label: 'Checked',
                  value: c.totalChecked,
                  onClick: () => setDetail({ title: `${c.label} — Checked`, items: c.checkedItems })
                },
                {
                  label: 'Rejected',
                  value: c.totalRejected,
                  onClick: () => setDetail({ title: `${c.label} — Rejected`, items: c.rejectedItems })
                }
              ]}
            />
          ))}
        </div>
      )}

      <QualityItemsDetailModal
        show={!!detail}
        onHide={() => setDetail(null)}
        title={detail?.title || ''}
        items={detail?.items || []}
        columns={REMAKE_ITEM_COLUMNS}
      />
    </section>
  );
}

QualityRemakesSummary.propTypes = {
  date: PropTypes.string.isRequired
};
